import DOMPurify from 'dompurify';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { COMPONENT_PATTERNS, PromptComponentPart, renderPromptComponent } from './prompt-components';
import { md, PROSE_CLASSES } from './RenderedMarkdown';

const PROMPT_COMPONENT_PLACEHOLDER_CLASS = 'prompt-component-placeholder';
const COMPONENT_PATTERN_SOURCE = COMPONENT_PATTERNS.map(({ regex }) => regex.source).join('|');

export const UserPromptContent: React.FC<{ content: string; className?: string }> = ({ content, className }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [targets, setTargets] = useState<{ el: Element; part: PromptComponentPart }[]>([]);

    const html = useMemo(() => {
        const rendered = md.render(content).replace(new RegExp(COMPONENT_PATTERN_SOURCE, 'g'), (match) => {
            for (const { regex, type } of COMPONENT_PATTERNS) {
                const m = regex.exec(match);
                if (m) {
                    return `<span class="${PROMPT_COMPONENT_PLACEHOLDER_CLASS}" data-type="${type}" data-payload="${m.at(1)}"></span>`;
                }
            }
            return match;
        });
        return DOMPurify.sanitize(rendered);
    }, [content]);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const parts = Array.from(
            containerRef.current.querySelectorAll<HTMLElement>(`.${PROMPT_COMPONENT_PLACEHOLDER_CLASS}`),
        ).flatMap((el) => {
            const type = el.dataset.type as PromptComponentPart['type'];
            const payload = el.dataset.payload;
            return type && payload ? [{ el, part: { type, guid: payload } }] : [];
        });
        setTargets(parts);
    }, [html]);

    return (
        <>
            <div ref={containerRef} className={className ?? PROSE_CLASSES} dangerouslySetInnerHTML={{ __html: html }} />
            {targets.map(({ el, part }) => createPortal(renderPromptComponent(part), el))}
        </>
    );
};
